/*
 * This file is part of glowingblue/orell-rezensionen.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import DiscussionList from 'flarum/forum/components/DiscussionList';
import Placeholder from 'flarum/common/components/Placeholder';
import ReviewItem from './ReviewItem';
import icon from 'flarum/common/helpers/icon';
import reviewExportState from '../utils/reviewExportState';
import Tooltip from 'flarum/common/components/Tooltip';
import Switch from 'flarum/common/components/Switch';
import { utils } from '@glowingblue-orell-thalia-common';
const { Auth } = utils;
import type { ComponentAttrs } from 'flarum/common/Component';
import type Discussion from 'flarum/common/models/Discussion';
import type Mithril from 'mithril';
import ReviewListState from '../states/ReviewListState';

export interface ReviewListAttrs extends ComponentAttrs {
	state: ReviewListState;
	/** Renders the per-review export toggle column. */
	showExport?: boolean;
	/** Forwarded to every `ReviewItem`. */
	noCover?: boolean;
}

export default class ReviewList extends DiscussionList {
	// `DiscussionList` is not generic over its attrs, so this narrows it.
	attrs!: ReviewListAttrs;

	/** Discussion id → whether its review is currently being exported. */
	exporting!: Record<string, boolean>;

	oninit(vnode: Mithril.Vnode<ComponentAttrs, this>) {
		super.oninit(vnode);

		this.exporting = {};
	}

	view() {
		const vnode = super.view();

		const state = this.attrs.state;

		if (state.isEmpty()) {
			let text = app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.empty_text');
			return (
				<div className="DiscussionList">
					<Placeholder text={text} />
				</div>
			);
		}

		const { showExport } = this.attrs;

		let key: string | undefined;
		if (showExport) {
			key = String(Math.random());
		}

		(vnode.children as Mithril.ChildArray)[0] = (
			<ul className="DiscussionList-discussions">
				{state.getPages().map((pg) => {
					return pg.items.map((discussion) => {
						const { exported, blocked } = reviewExportState(discussion);

						return (
							<li data-id={discussion.id()} key={discussion.id()}>
								<ReviewItem discussion={discussion} noCover={this.attrs.noCover} key={key ? discussion.id() : undefined} />
								{showExport && (
									<div className={`ReviewItem-exportToggle ${blocked ? 'disabled' : ''}`} key={key}>
										<Tooltip text={this.getExportTooltipText(discussion)} position="bottom" key={key}>
											<div>
												{icon(this.getExportIcon(discussion))}
												<Switch
													state={exported || discussion.selectedForExport()}
													disabled={this.exporting[discussion.id()!] || exported || blocked}
													loading={this.exporting[discussion.id()!]}
													onchange={(value: boolean) => this.selectReviewForExport(discussion, value)}
												/>
											</div>
										</Tooltip>
									</div>
								)}
							</li>
						);
					});
				})}
			</ul>
		);

		return vnode;
	}

	getExportTooltipText(discussion: Discussion) {
		const { exported, locked, unavailable, notApproved, hidden } = reviewExportState(discussion);

		if (locked) {
			return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.export_tooltip_locked');
		}

		if (unavailable) {
			return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.export_tooltip_not_available');
		}

		if (notApproved) {
			return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.export_tooltip_not_approved');
		}

		if (hidden) {
			return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.export_tooltip_hidden');
		}

		if (exported) {
			return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.exported_tooltip');
		}
		return app.translator.trans('glowingblue-orell-rezensionen.forum.review_list.export_tooltip');
	}

	getExportIcon(discussion: Discussion) {
		const { exported, locked, unavailable } = reviewExportState(discussion);

		if (locked) {
			return 'fa-kit fa-regular-cloud-lock';
		}

		if (unavailable) {
			return 'fa-kit fa-regular-cloud-slash';
		}

		if (exported) {
			return 'fa-kit fa-solid-cloud-circle-check';
		}

		return 'fa-kit fa-regular-cloud-circle-arrow-up';
	}

	async selectReviewForExport(discussion: Discussion, selected: boolean) {
		if (!app.session.user) {
			return;
		}

		if (!app.session.user.attribute('linkedShop')) {
			await Auth.linkAccount(app.translator.trans('glowingblue-orell-rezensionen.forum.user.reviews.export_connect'), ReviewListState.gaExportAttrs);
		}

		if (reviewExportState(discussion).exported) {
			return;
		}

		this.attrs.state.hideExportedReport();

		discussion.pushAttributes({ selectedForExport: selected });
		m.redraw();
	}
}
